// Scene-JSON orchestrator. Fetches scene.json, mutates the viewer's
// sceneMeta/grid buckets, dispatches the appropriate intents (slice/grid
// visibility, default field component), and delegates scene-builder concerns
// to processSceneObjects. Pure parsers live in ./parsers; async colormap +
// theme loaders live in ../loaders/.
//
// The function is large by necessity — it's the conductor for every option
// scene.json declares. Internal helpers below split the work by concern.

import * as THREE from 'three';
import { store } from '../../store/instance';
import { setCoordinateRotationMatrix, mapPhysicalToThreeWithRotation } from '@adapters/coordinates';
import { applyTheme } from '../theme';
import { uploadedDatasets } from '../uploadedDatasets';
import { INTENTS } from '../../store/intents';
import { debugWarn } from '@adapters/debug';
import { asObj, asNum, asStr, asBool } from './parsers';
import { processSceneObjects } from './spheres';
import { loadColormapsFromScene } from '../loaders/colormaps';
import { loadThemesFromScene } from '../loaders/themes';
import type { SceneJson } from './parsers';
import type { ViewerContext, UiDriver } from '../context';
import type { ObjectRegistry } from '../objectRegistry';

export { parseRotationToMatrix3 } from './parsers';
export { processSceneObjects } from './spheres';
export { loadColormapsFromScene } from '../loaders/colormaps';
export { loadThemesFromScene } from '../loaders/themes';

function applyTopLevelMeta(viewer: ViewerContext, sceneConf: SceneJson): void {
    const coord = asStr(sceneConf['coordinate']);
    if (coord) viewer.sceneMeta.coordinateSystem = coord;
    const units = asStr(sceneConf['units']);
    if (units) viewer.sceneMeta.units = units;
    const debug = asBool(sceneConf['debug']);
    if (debug !== undefined) viewer.sceneMeta.debugEnabled = debug;
    const showFPS = asBool(sceneConf['showFPS']);
    if (showFPS !== undefined) viewer.sceneMeta.showFps = showFPS;

    const uiConf = asObj(sceneConf['ui']);
    if (uiConf) {
        const showTheme = asBool(uiConf['showThemeSection']);
        if (showTheme !== undefined) viewer.sceneMeta.ui.showThemeSection = showTheme;
        const showSel = asBool(uiConf['showSelectionSection']);
        if (showSel !== undefined) viewer.sceneMeta.ui.showSelectionSection = showSel;
        const driver = asStr(uiConf['driver']);
        if (driver === 'panel' || driver === 'headless') viewer.sceneMeta.ui.driver = driver as UiDriver;
    }
}

function applyAxisConfig(viewer: ViewerContext, axis: Record<string, unknown>): void {
    viewer.sceneMeta.axisConfigPresent = true;
    viewer.sceneMeta.showCoordAxes = !!axis['showCoordinateAxes'];
    if (axis['axesVectors']) viewer.sceneMeta.axesVectorsConfig = axis['axesVectors'] as Record<string, unknown>;
    const labelSize = asNum(axis['labelSize']) ?? asNum(axis['gridLabelSize']);
    if (labelSize !== undefined) viewer.sceneMeta.axis.labelSize = labelSize;
    viewer.sceneMeta.axis.gridLabelSize = viewer.sceneMeta.axis.labelSize;
    const minorOp = asNum(axis['gridMinorOpacity']);
    if (minorOp !== undefined) viewer.sceneMeta.axis.gridMinorOpacity = minorOp;
    const majorOp = asNum(axis['gridMajorOpacity']);
    if (majorOp !== undefined) viewer.sceneMeta.axis.gridMajorOpacity = majorOp;
    const minorFoot = asNum(axis['gridMinorFoot']);
    if (minorFoot !== undefined) viewer.sceneMeta.axis.gridMinorFoot = minorFoot;
    const majorFoot = asNum(axis['gridMajorFoot']);
    if (majorFoot !== undefined) viewer.sceneMeta.axis.gridMajorFoot = majorFoot;
    const labelPad = asNum(axis['labelPadding']);
    if (labelPad !== undefined) viewer.sceneMeta.axis.labelExtraPadding = labelPad;

    if (asObj(axis['extents'])) {
        const e = asObj(axis['extents'])!;
        const x = e['x'] as { min: number; max: number };
        const y = e['y'] as { min: number; max: number };
        const z = e['z'] as { min: number; max: number };
        viewer.grid.axisExtents = {
            x: { min: x.min, max: x.max },
            y: { min: y.min, max: y.max },
            z: { min: z.min, max: z.max },
        };
    }
    if (typeof axis['spacing'] === 'number') viewer.grid.gridSpacing = axis['spacing'] as number;
    if (typeof axis['minor_spacing'] === 'number') viewer.grid.minorSpacing = axis['minor_spacing'] as number;
    if (typeof axis['major_spacing'] === 'number') viewer.grid.majorSpacing = axis['major_spacing'] as number;

    store.dispatch(INTENTS.SET_SHOW_GRID, { plane: 'XY', value: axis['xy-grid'] !== false });
    store.dispatch(INTENTS.SET_SHOW_GRID, { plane: 'XZ', value: !!axis['xz-grid'] });
    store.dispatch(INTENTS.SET_SHOW_GRID, { plane: 'YZ', value: !!axis['yz-grid'] });
}

function applySelectionConfig(viewer: ViewerContext, sceneConf: SceneJson): void {
    const sel = viewer.sceneMeta.selection;
    const sphereRadius = asNum(sceneConf['selectionSphereRadius']);
    if (sphereRadius !== undefined) sel.sphereRadius = sphereRadius;
    const selPos = asObj(sceneConf['selectionPosition']);
    if (selPos && typeof selPos['x'] === 'number' && typeof selPos['y'] === 'number' && typeof selPos['z'] === 'number') {
        sel.position = mapPhysicalToThreeWithRotation(new THREE.Vector3(selPos['x'] as number, selPos['y'] as number, selPos['z'] as number));
    }
    const projOp = asNum(sceneConf['selectionProjectionOpacity']);
    if (projOp !== undefined) sel.projectionOpacity = projOp;
    if (typeof sceneConf['selectionProjectionColor'] !== 'undefined') {
        try {
            const v = sceneConf['selectionProjectionColor'];
            sel.projectionColor = (typeof v === 'string')
                ? new THREE.Color(v).getHex()
                : (v as number);
        } catch { /* swallow */ }
    }
}

function applyCameraPosition(viewer: ViewerContext, sceneConf: SceneJson): void {
    const camPos = asObj(sceneConf['camera-position']);
    if (!camPos) return;
    const c = camPos as { x: number; y: number; z: number };
    viewer.sceneMeta.cameraGSM = new THREE.Vector3(c.x, c.y, c.z);
    const camera = viewer.three.camera;
    if (camera) {
        camera.position.copy(mapPhysicalToThreeWithRotation(viewer.sceneMeta.cameraGSM));
        camera.updateProjectionMatrix();
    }
}

function applyDatasetMap(viewer: ViewerContext, sceneConf: SceneJson): void {
    if (!Array.isArray(sceneConf['datasets'])) return;
    viewer.sceneMeta.datasetMap = {};
    (sceneConf['datasets'] as Array<{ name?: string; url?: string }>).forEach((d) => {
        if (d && d.name && d.url) viewer.sceneMeta.datasetMap[d.name] = { url: d.url };
    });
    for (const [name, url] of uploadedDatasets) {
        viewer.sceneMeta.datasetMap[name] = { url };
    }
}

function applyFieldlineConfig(viewer: ViewerContext, fl: Record<string, unknown>): void {
    const fc = viewer.sceneMeta.fieldlineConfig;
    if (typeof fl['color'] !== 'undefined') {
        try { fc.color = (typeof fl['color'] === 'string') ? new THREE.Color(fl['color'] as string).getHex() : fl['color'] as number; } catch { /* swallow */ }
    }
    if (typeof fl['coneLength'] === 'number') fc.coneLength = fl['coneLength'] as number;
    if (typeof fl['coneRadius'] === 'number') fc.coneRadius = fl['coneRadius'] as number;
    if (typeof fl['coneColor'] !== 'undefined') {
        try { fc.coneColor = (typeof fl['coneColor'] === 'string') ? new THREE.Color(fl['coneColor'] as string).getHex() : fl['coneColor'] as number; } catch { /* swallow */ }
    }
    if (typeof fl['opacity'] === 'number') fc.opacity = fl['opacity'] as number;
    if (typeof fl['coneOpacity'] === 'number') fc.coneOpacity = fl['coneOpacity'] as number;
    if (Array.isArray(fl['seeds'])) {
        viewer.objects.fieldlines.seeds = (fl['seeds'] as Array<Record<string, unknown>>).map((s) => new THREE.Vector3(
            +(s['x'] as number),
            +(s['y'] as number),
            +(s['z'] as number),
        )).filter((v) => Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z));
        const rawSeeds = fl['seeds'] as Array<Record<string, unknown>>;
        viewer.objects.fieldlines.seeds.forEach((vec, i) => {
            const s = rawSeeds[i];
            if (!s) return;
            (vec as unknown as { step?: number; maxSteps?: number }).step = (typeof s['step'] === 'number') ? s['step'] as number : 0.1;
            (vec as unknown as { step?: number; maxSteps?: number }).maxSteps = (typeof s['maxSteps'] === 'number') ? s['maxSteps'] as number : 5000;
        });
    }
}

/** Fetch a scene.json from `url` and apply it to viewer. */
export async function loadSceneJson(viewer: ViewerContext, url: string): Promise<boolean> {
    const sceneRegistry = viewer.objects.sceneRegistry as unknown as ObjectRegistry | null;
    const registerSceneObject = sceneRegistry ? sceneRegistry.register : undefined;
    const rotatingObjects = viewer.objects.rotating;
    const loadTheme = (themeUrl: string): Promise<unknown> => applyTheme(viewer, themeUrl);
    try {
        const res = await fetch(url, { cache: 'no-cache' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const sceneConf = await res.json() as SceneJson;

        applyTopLevelMeta(viewer, sceneConf);

        const axis = asObj(sceneConf['axis']);
        if (axis && typeof axis['showXY'] === 'boolean') store.dispatch(INTENTS.SET_SLICE_VISIBILITY, { axis: 'XY', visible: axis['showXY'] });
        if (axis && typeof axis['showZY'] === 'boolean') store.dispatch(INTENTS.SET_SLICE_VISIBILITY, { axis: 'ZY', visible: axis['showZY'] });
        if (axis && typeof axis['showZX'] === 'boolean') store.dispatch(INTENTS.SET_SLICE_VISIBILITY, { axis: 'ZX', visible: axis['showZX'] });

        if (sceneConf['rotation']) {
            const { parseRotationToMatrix3 } = await import('./parsers');
            const m3 = parseRotationToMatrix3(sceneConf['rotation']);
            if (m3) setCoordinateRotationMatrix(m3);
        }

        if (axis) applyAxisConfig(viewer, axis);
        applySelectionConfig(viewer, sceneConf);

        if (typeof sceneConf['theme'] === 'string') await loadTheme(sceneConf['theme']);
        const helpStart = asBool(sceneConf['help-start']);
        if (helpStart !== undefined) viewer.sceneMeta.helpStart = helpStart;
        const devMode = asBool(sceneConf['developer-mode']);
        if (devMode !== undefined) viewer.sceneMeta.developerMode = devMode;

        applyCameraPosition(viewer, sceneConf);

        const lighting = asObj(sceneConf['lighting']);
        if (lighting) viewer.sceneMeta.lighting = lighting as unknown as ViewerContext['sceneMeta']['lighting'];

        if (Array.isArray(sceneConf['colormaps'])) {
            await loadColormapsFromScene(sceneConf, viewer.colormap.defs as unknown as Record<string, unknown>, { debugEnabled: viewer.sceneMeta.debugEnabled });
        }

        applyDatasetMap(viewer, sceneConf);

        if (Array.isArray(sceneConf['themes'])) {
            await loadThemesFromScene(sceneConf, viewer.theme.options, loadTheme, { debugEnabled: viewer.sceneMeta.debugEnabled });
        }

        const fl = asObj(sceneConf['fieldLines']);
        if (fl) applyFieldlineConfig(viewer, fl);

        if (typeof sceneConf['defaultFieldComponent'] === 'string') {
            store.dispatch(INTENTS.SET_FIELD_COMPONENT, { component: sceneConf['defaultFieldComponent'] as string });
        }

        processSceneObjects(sceneConf, viewer, { rotatingObjects, registerSceneObject });
        return true;
    } catch (e) {
        debugWarn(viewer, 'Failed to load scene JSON', e);
        return false;
    }
}
